import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import Notification from "@source/components/snackMessage/notification";
import appStore from "@source/store/appStore";
import ResidentController from "@source/api/controllers/residentController";
import EmployeeController from "@source/api/controllers/employeeController";
import ConsentController from "@source/api/controllers/consentController";
import {
  ConsentCategory,
  ConsentCategorySource,
  ConsentCategoryStatus,
  ConsentClinicalSigningMode,
  ConsentDocument,
  ConsentDocumentStatus,
  ConsentSignerRole,
  ConsentTemplate,
} from "@source/api/models/consentModels";
import {
  ConsentStepsValues,
  createConsentStepsPayload,
  emptyConsentStepsValues,
  hasValidConfirmedConsentPdf,
  isClinicalOnSiteImmediatelyActiveAfterSend,
  isResidentImmediatelyActiveAfterSend,
  normalizeConsentIds,
  sortConsentInputsByDocument,
  validateConsentCategorySelection,
  validateConsentPdfFile,
  validateConsentPdfFiles,
  validateConsentSteps,
} from "@source/api/models/consentHelpers";
import useIncrementalConsentList from "@source/hooks/useIncrementalConsentList";
import {ConsentRequestState} from "@source/components/consentDocuments";
import {CategoryStep} from "./categoryStep";
import {DocumentStep} from "./documentStep";
import {SignerStep} from "./signerStep";
import {SentConsentStep} from "./sentConsentStep";
import {templatePreviewDocument} from "./consentStepsPreview";
import {
  SignerPerson,
  PersistStage,
  persistStageText,
  actionableConsentError,
  activeFamilySigners,
  categoryNeedsSignerSelection,
  consentStepCount,
  consentStepSubtitle,
  fileHasPdfHeader,
  incompleteStaffInputs,
  rolesText,
  valuesFromDraft,
} from "./consentStepsShared";
import styles from "./consentSteps.module.scss";

export const ConsentSteps = ({nursingHomeId, organizationId}: {nursingHomeId: string; organizationId: string}) => {
  const history = useHistory();
  const {residentId, documentId} = useParams<{residentId: string; documentId?: string}>();
  const [step, setStep] = useState(1);
  const [values, setValues] = useState<ConsentStepsValues>(emptyConsentStepsValues);
  const [draft, setDraft] = useState<ConsentDocument | null>(null);
  const [resident, setResident] = useState<any>(null);
  const [employees, setEmployees] = useState<SignerPerson[]>([]);
  const [template, setTemplate] = useState<ConsentTemplate | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [formError, setFormError] = useState("");
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({});
  const [stale, setStale] = useState(false);
  const [persistStage, setPersistStage] = useState<PersistStage>(null);
  const [selectedPdf, setSelectedPdf] = useState<File | null>(null);
  const [sentDocument, setSentDocument] = useState<ConsentDocument | null>(null);
  const [supportingCategory, setSupportingCategory] = useState<ConsentCategory | null>(null);
  const mountedRef = useRef(true);
  const detailsRequestRef = useRef(0);
  const templateRequestRef = useRef(0);
  const persistRequestRef = useRef(0);
  const persistingRef = useRef(false);
  const categoryRequest = useMemo(() => (query: {page: number; limit: number}) =>
    ConsentController.listCategories(organizationId, {...query, status: ConsentCategoryStatus.ACTIVE, sort: "asc"}), [organizationId]);
  const categories = useIncrementalConsentList<ConsentCategory, {}>(categoryRequest, {});
  const selected = categories.items.find((category) => category._id === values.categoryId) || null;
  const selectedSnapshot = draft?.categorySnapshot?.categoryId === values.categoryId ? draft.categorySnapshot : null;
  const selectedCategory = selected || selectedSnapshot;
  const needsSigners = categoryNeedsSignerSelection(selectedCategory);
  const stepCount = consentStepCount(selectedCategory);
  const cancel = useCallback(
    () => history.push(`/home/residents/${residentId}/consent-documents`),
    [history, residentId],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      persistRequestRef.current += 1;
    };
  }, []);
  useEffect(() => {
    if (step > stepCount) setStep(stepCount);
  }, [step, stepCount]);
  useEffect(() => {
    const residentName = [resident?.firstName, resident?.lastName].filter(Boolean).join(" ") || "Resident";
    const visibleStep = Math.min(step, stepCount);
    const header = {
      resident: resident || undefined,
      title: `${documentId ? "Edit" : "New"} consent document for ${residentName}`,
      subtitle: `Step ${visibleStep} of ${stepCount} · ${consentStepSubtitle(visibleStep)}`,
      onBack: sentDocument ? cancel : visibleStep > 1 ? () => setStep(visibleStep - 1) : cancel,
      usePreviousStepNavigation: true,
      onCancel: cancel,
    };
    appStore.setConsentResidentHeader(header);
    return () => appStore.resetConsentResidentHeader(header);
  }, [documentId, resident, sentDocument, step, stepCount, cancel]);

  const loadDetails = useCallback(async (reset = false) => {
    const requestId = ++detailsRequestRef.current;
    if (!mountedRef.current) return;
    if (reset) {
      templateRequestRef.current += 1;
      setStep(1);
      setValues(emptyConsentStepsValues());
      setDraft(null);
      setResident(null);
      setEmployees([]);
      setTemplate(null);
      setTemplateError("");
      setFormError("");
      setInputErrors({});
      setStale(false);
      setPersistStage(null);
      setSelectedPdf(null);
      setSentDocument(null);
      setSupportingCategory(null);
    }
    setLoadingDetails(true);
    setDetailError("");
    try {
      const [loadedResident, loadedEmployees, loadedDraft] = await Promise.all([
        ResidentController.getResident({nursingHomeId, residentId}),
        EmployeeController.getEmployees({
          searchString: "",
          organizationId,
          isDisabled: "false",
          isConfirmed: "",
          limit: 999999,
          page: 0,
          nursingHomeIds: [nursingHomeId],
        }),
        documentId ? ConsentController.getDocument(nursingHomeId, documentId) : Promise.resolve(null),
      ]);
      if (!mountedRef.current || requestId !== detailsRequestRef.current) return;
      if (loadedDraft && loadedDraft.resident._id !== residentId) {
        new Notification().error("This draft belongs to a different resident and cannot be edited from this URL.");
        history.replace(`/home/residents/${residentId}/consent-documents`);
        return;
      }
      if (loadedDraft && loadedDraft.status !== ConsentDocumentStatus.DRAFT) {
        new Notification().error("This consent document is no longer a draft and cannot be edited.");
        history.replace(`/home/residents/${residentId}/consent-documents`);
        return;
      }
      const activatedEmployees = (loadedEmployees.users || []).filter((person) => person.isDisabled !== true);
      const activatedIds = new Set(activatedEmployees.map((person) => person._id));
      const nextValues = loadedDraft ? valuesFromDraft(loadedDraft) : emptyConsentStepsValues();
      setResident(loadedResident);
      setEmployees(activatedEmployees);
      setDraft(loadedDraft);
      setValues({
        ...nextValues,
        employeeSignerIds: nextValues.employeeSignerIds.filter((id) => activatedIds.has(id)),
        clinicalSignerId: activatedIds.has(nextValues.clinicalSignerId) ? nextValues.clinicalSignerId : "",
      });
      setFormError("");
      setStale(false);
    } catch (error: unknown) {
      if (mountedRef.current && requestId === detailsRequestRef.current) {
        setDetailError(actionableConsentError(error, "Unable to load consent details."));
      }
    } finally {
      if (mountedRef.current && requestId === detailsRequestRef.current) setLoadingDetails(false);
    }
  }, [documentId, history, nursingHomeId, organizationId, residentId]);
  useEffect(() => { void loadDetails(true); }, [loadDetails]);

  const loadTemplate = useCallback(async () => {
    const requestId = ++templateRequestRef.current;
    if (selectedCategory?.source !== ConsentCategorySource.TEMPLATE || !selectedCategory.templateId) {
      if (mountedRef.current) {
        setTemplate(null);
        setTemplateError("");
      }
      return;
    }
    setTemplate(null);
    setTemplateError("");
    try {
      const loaded = await ConsentController.getTemplate(organizationId, selectedCategory.templateId);
      if (mountedRef.current && requestId === templateRequestRef.current) setTemplate(loaded);
    } catch (error: unknown) {
      if (mountedRef.current && requestId === templateRequestRef.current) {
        setTemplateError(actionableConsentError(error, "Unable to load the selected template."));
      }
    }
  }, [organizationId, selectedCategory?.source, selectedCategory?.templateId]);
  useEffect(() => { if (step >= 2) void loadTemplate(); }, [loadTemplate, step]);
  useEffect(() => {
    if (step < 2 || !values.categoryId) return;
    let cancelled = false;
    ConsentController.getCategory(organizationId, values.categoryId)
      .then((category) => { if (!cancelled) setSupportingCategory(category); })
      .catch(() => { if (!cancelled) setSupportingCategory(null); });
    return () => { cancelled = true; };
  }, [organizationId, step, values.categoryId]);

  const chooseCategory = (id: string) => {
    if (id !== values.categoryId) setSelectedPdf(null);
    setValues((current) => {
      if (id === current.categoryId) return current;
      const next = categories.items.find((category) => category._id === id);
      const compatible = (role: ConsentSignerRole) => Boolean(next?.requiredSignerRoles.includes(role));
      return {
        ...current,
        categoryId: id,
        employeeInputValues: {},
        familyFieldCompleterId: "",
        employeeSignerIds: compatible(ConsentSignerRole.EMPLOYEE) ? current.employeeSignerIds : [],
        familyMemberIds: compatible(ConsentSignerRole.FAMILY_MEMBER) ? current.familyMemberIds : [],
        clinicalSigningMode: compatible(ConsentSignerRole.GP_CLINICAL) ? current.clinicalSigningMode : "",
        clinicalSignerId: compatible(ConsentSignerRole.GP_CLINICAL) ? current.clinicalSignerId : "",
      };
    });
  };
  const addSigner = (key: "employeeSignerIds" | "familyMemberIds", id: string) => {
    if (key === "employeeSignerIds" && id === values.clinicalSignerId) {
      setFormError("The GP / Clinician cannot also be a required employee signer.");
      return;
    }
    if (key === "employeeSignerIds") setFormError("");
    setValues((current) => ({...current, [key]: normalizeConsentIds([...current[key], id])}));
  };
  const selectClinicalSigner = (id: string) => {
    if (values.employeeSignerIds.includes(id)) {
      setFormError("The GP / Clinician cannot also be a required employee signer.");
      return;
    }
    setFormError("");
    setValues((current) => ({...current, clinicalSignerId: id}));
  };
  const removeSigner = (key: "employeeSignerIds" | "familyMemberIds", id: string) => setValues((current) => {
    const remaining = current[key].filter((item) => item !== id);
    return {
      ...current,
      [key]: remaining,
      ...(key === "familyMemberIds" && (current.familyFieldCompleterId === id || remaining.length <= 1)
        ? {familyFieldCompleterId: ""}
        : {}),
    };
  });
  const changeInput = (id: string, value: unknown) => {
    setInputErrors((current) => {
      if (!current[id]) return current;
      const next = {...current};
      delete next[id];
      return next;
    });
    setValues((current) => ({...current, employeeInputValues: {...current.employeeInputValues, [id]: value}}));
  };
  const selectPdf = (file: File | null) => {
    if (!file) {
      setSelectedPdf(null);
      return;
    }
    if (validateConsentPdfFile(file)) {
      setSelectedPdf(null);
      return;
    }
    setSelectedPdf(file);
    setFormError("");
  };
  const previewDocument = useMemo(
    () => (template ? templatePreviewDocument(template, resident, values) : ""),
    [resident, template, values],
  );
  const orderedEmployeeInputs = useMemo(
    () => sortConsentInputsByDocument(template?.employeeInputs ?? [], template?.structure?.html, "employee"),
    [template],
  );
  const hasConfirmedPdf = hasValidConfirmedConsentPdf(draft, values.categoryId, residentId);
  const showSavedPdfPreview = Boolean(
    draft && draft.resident._id === residentId
    && (draft.categoryId || draft.categorySnapshot?.categoryId) === values.categoryId
    && (selectedCategory?.source === ConsentCategorySource.TEMPLATE || hasConfirmedPdf),
  );

  const persist = async (send: boolean) => {
    if (persistStage || persistingRef.current) return;
    const error = !selected
      ? (selectedSnapshot && !categories.isLoading && !categories.hasMore
        ? "This Consent Category is no longer active. Select another active category."
        : "Wait for active Consent Categories to finish loading.")
      : validateConsentSteps(selectedCategory, template, values, send)
        || (send && selectedCategory?.source === ConsentCategorySource.RAW_PDF && !selectedPdf && !hasConfirmedPdf
          ? "Attach a PDF before sending this consent document."
          : "");
    const incomplete = send ? incompleteStaffInputs(orderedEmployeeInputs, values.employeeInputValues) : [];
    const returnToDocument = (message: string) => {
      setStep(2);
      setFormError(message);
    };
    if (error === "Attach a PDF before sending this consent document.") {
      setInputErrors({});
      returnToDocument(error);
      return;
    }
    if (error && error !== "Complete every staff text field before sending.") {
      setFormError(error);
      setInputErrors({});
      return;
    }
    if (incomplete[0]) {
      setInputErrors(Object.fromEntries(incomplete.map((input) => [
        input.id,
        input.type === "radio" ? "Select an option before sending." : "Complete this field before sending.",
      ])));
      returnToDocument("Complete the highlighted field before sending.");
      window.setTimeout(() => {
        document.getElementById(`consent-field-${incomplete[0].id}`)?.scrollIntoView({behavior: "smooth", block: "center"});
      }, 0);
      return;
    }
    if (error) {
      returnToDocument(error);
      return;
    }
    if (!selectedCategory || (selectedCategory.source === ConsentCategorySource.TEMPLATE && (!template || templateError))) {
      returnToDocument(templateError || "Wait for the selected template to load, then try again.");
      return;
    }
    persistingRef.current = true;
    const requestId = ++persistRequestRef.current;
    let stage: Exclude<PersistStage, null> = "saving";
    const setStage = (next: Exclude<PersistStage, null>) => {
      stage = next;
      setPersistStage(next);
    };
    setStage("saving");
    setFormError("");
    setStale(false);
    try {
      const payload = createConsentStepsPayload(residentId, selectedCategory, template, values);
      let result = draft
        ? await ConsentController.updateDocument(nursingHomeId, draft._id, payload)
        : await ConsentController.createDocument(nursingHomeId, payload);
      setDraft(result);
      if (selectedPdf) {
        if (!(await fileHasPdfHeader(selectedPdf))) throw new Error("The selected file does not have a valid PDF header.");
        setStage("uploading");
        const target = await ConsentController.createPdfUploadUrl(nursingHomeId, result._id);
        await ConsentController.uploadPdf(target.uploadUrl, selectedPdf);
        setStage("confirming");
        result = await ConsentController.confirmPdf(nursingHomeId, result._id, {
          objectKey: target.objectKey,
          originalFileName: selectedPdf.name,
          mimeType: "application/pdf",
        });
        setDraft(result);
        setSelectedPdf(null);
      }
      if (send) {
        setStage("sending");
        result = await ConsentController.sendDocument(nursingHomeId, result._id);
      }
      if (!mountedRef.current || requestId !== persistRequestRef.current) return;
      try {
        new Notification().success(send ? "Consent document sent." : "Consent document saved as draft.");
      } catch {
        // Notification failures must not recast a completed mutation as an error.
      }
      if (send && (isResidentImmediatelyActiveAfterSend(result) || isClinicalOnSiteImmediatelyActiveAfterSend(result))) {
        setSentDocument(result);
      } else {
        history.push(`/home/residents/${residentId}/consent-documents`);
      }
    } catch (requestError: unknown) {
      const message = actionableConsentError(requestError, "Unable to save the consent document.");
      const status = typeof requestError === "object" && requestError !== null && "status" in requestError
        ? (requestError as {status?: number}).status
        : undefined;
      setFormError(status === 409 ? message : `${persistStageText[stage]} failed: ${message}`);
      setStale(status === 409);
    } finally {
      persistingRef.current = false;
      if (mountedRef.current && requestId === persistRequestRef.current) setPersistStage(null);
    }
  };

  if (loadingDetails) return <ConsentRequestState variant="loading" title="Loading Consent Steps"/>;
  if (detailError) return <ConsentRequestState variant="error" title="Unable to load Consent Steps" description={detailError} onRetry={() => void loadDetails()}/>;
  if (sentDocument) return <SentConsentStep document={sentDocument} residentId={residentId} onLater={cancel}/>;

  const family = activeFamilySigners(resident);
  const supportingDocuments = (supportingCategory?._id === values.categoryId
    ? supportingCategory.supportingDocuments
    : selected?.supportingDocuments) ?? [];
  const continueToSigners = () => {
    if (selectedCategory?.source === ConsentCategorySource.TEMPLATE && (templateError || !template)) {
      setFormError(templateError || "Wait for the selected template to load, then try again.");
      return;
    }
    setFormError("");
    setStep(3);
  };
  const visibleStep = Math.min(step, stepCount);

  return <div className={styles.steps}>
    {visibleStep === 1 ? <CategoryStep
      categories={categories}
      categoryId={values.categoryId}
      selected={selected}
      unavailableName={selectedSnapshot?.name}
      categoryUnavailable={Boolean(selectedSnapshot && !selected && !categories.isLoading && !categories.hasMore)}
      disabled={Boolean(persistStage)}
      error={formError}
      onChange={chooseCategory}
      onCancel={cancel}
      onContinue={() => {
        const categoryError = validateConsentCategorySelection(selected, Boolean(selectedSnapshot && !selected && !categories.isLoading && !categories.hasMore));
        if (categoryError) setFormError(categoryError);
        else {
          setFormError("");
          setStep(2);
        }
      }}
    /> : null}
    {visibleStep === 2 ? <DocumentStep
      categoryName={selectedCategory?.name}
      source={selectedCategory?.source}
      template={template}
      templateError={templateError}
      previewDocument={previewDocument}
      supportingDocuments={supportingDocuments}
      inputs={orderedEmployeeInputs}
      values={values}
      inputErrors={inputErrors}
      draftFileName={draft?.pdf?.originalFileName}
      draftId={draft?._id}
      showSavedPdfPreview={showSavedPdfPreview && selectedCategory?.source === ConsentCategorySource.RAW_PDF}
      hasConfirmedPdf={hasConfirmedPdf}
      selectedPdf={selectedPdf}
      persistStage={persistStage}
      formError={formError}
      needsSigners={needsSigners}
      stale={stale}
      onSelectPdf={selectPdf}
      validatePdfFiles={validateConsentPdfFiles}
      onInputChange={changeInput}
      onRetryTemplate={() => void loadTemplate()}
      onCancel={cancel}
      onContinue={continueToSigners}
      onSave={() => void persist(false)}
      onSend={() => void persist(true)}
      onRefresh={() => void loadDetails()}
    /> : null}
    {visibleStep === 3 && needsSigners ? <SignerStep
      categoryName={selectedCategory?.name}
      rolesTextValue={selectedCategory ? rolesText(selectedCategory) : ""}
      source={selectedCategory?.source}
      requiredRoles={selectedCategory?.requiredSignerRoles || []}
      values={values}
      employees={employees}
      family={family}
      persistStage={persistStage}
      formError={formError}
      stale={stale}
      onClinicalMode={(mode) => setValues({
        ...values,
        clinicalSigningMode: mode,
        clinicalSignerId: mode === ConsentClinicalSigningMode.ON_SITE ? "" : values.clinicalSignerId,
      })}
      onAddSigner={addSigner}
      onRemoveSigner={removeSigner}
      onSelectClinicalSigner={selectClinicalSigner}
      onClearClinicalSigner={() => setValues({...values, clinicalSignerId: ""})}
      onFamilyCompleter={(id) => setValues({...values, familyFieldCompleterId: id})}
      onCancel={cancel}
      onSave={() => void persist(false)}
      onSend={() => void persist(true)}
      onRefresh={() => void loadDetails()}
    /> : null}
  </div>;
};
